/**
 * @fileoverview Tag - Compact label with optional leading content and remove action
 *
 * Purpose: Display removable labels, category tags, and items with icons/avatars
 *
 * Features:
 * - Leading content slot (icon, avatar, flag, etc.)
 * - Optional colored dot indicator
 * - Remove button with callback
 * - Three sizes (sm, md, lg)
 * - Always outline style with rounded-lg shape
 *
 * @example
 * <Tag>Plain text</Tag>
 * <Tag leading={<FlagIcon />}>Australia</Tag>
 * <Tag dot>Active</Tag>
 * <Tag dot="emerald">Online</Tag>
 * <Tag onRemove={() => ...}>Removable</Tag>
 */

import * as React from "react";
import { type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/utils/misc";
import { tagVariants } from "./tag.variants";

// ---------------------------------------------------------------------------
// Dot color map
// ---------------------------------------------------------------------------

const dotColors: Record<string, string> = {
  gray: "bg-gray-500",
  blue: "bg-blue-500",
  red: "bg-red-500",
  green: "bg-green-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  teal: "bg-teal-500",
  indigo: "bg-indigo-500",
  purple: "bg-purple-500",
  pink: "bg-pink-500",
  orange: "bg-orange-500",
  violet: "bg-violet-500",
  sky: "bg-sky-500",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface TagProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tagVariants> {
  /** Content rendered before the label (icon, avatar, flag, etc.) */
  leading?: React.ReactNode;
  /** Show a colored dot. Pass `true` for default gray, or a color name. */
  dot?: boolean | string;
  /** When provided, renders a remove (X) button that calls this callback */
  onRemove?: () => void;
}

function Tag({
  className,
  size,
  leading,
  dot,
  onRemove,
  children,
  ...props
}: TagProps) {
  const dotSize =
    size === "sm" ? "h-1.5 w-1.5" : size === "lg" ? "h-2.5 w-2.5" : "h-2 w-2";
  const dotColor =
    typeof dot === "string" ? (dotColors[dot] ?? "bg-gray-400") : "bg-gray-400";

  return (
    <div className={cn(tagVariants({ size }), className)} {...props}>
      {dot && (
        <span className={cn("shrink-0 rounded-full", dotSize, dotColor)} />
      )}
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="truncate">{children}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="shrink-0 rounded-sm opacity-60 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label="Remove"
        >
          <X
            className={
              size === "sm"
                ? "h-2.5 w-2.5"
                : size === "lg"
                  ? "h-4 w-4"
                  : "h-3 w-3"
            }
          />
        </button>
      )}
    </div>
  );
}

export { Tag };
